import { useState } from "react";
import { authApi } from "@/services/api";

interface ChangePasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * 본인 비밀번호 변경 모달.
 * 현재 비밀번호 확인 → 새 비밀번호(8자 이상) + 확인 입력 → POST /auth/change-password.
 */
export default function ChangePasswordModal({
  isOpen,
  onClose,
}: ChangePasswordModalProps) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  if (!isOpen) return null;

  const reset = () => {
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setError(null);
    setSuccess(false);
    setIsSubmitting(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    setError(null);
    if (!currentPassword || !newPassword) {
      setError("현재 비밀번호와 새 비밀번호를 입력하세요.");
      return;
    }
    if (newPassword.length < 8) {
      setError("새 비밀번호는 8자 이상이어야 합니다.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("새 비밀번호가 일치하지 않습니다.");
      return;
    }
    if (newPassword === currentPassword) {
      setError("새 비밀번호가 기존 비밀번호와 동일합니다.");
      return;
    }

    setIsSubmitting(true);
    try {
      await authApi.changePassword({
        current_password: currentPassword,
        new_password: newPassword,
      });
      setSuccess(true);
    } catch (err: unknown) {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? "비밀번호 변경에 실패했습니다. 다시 시도하세요.";
      setError(detail);
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClass =
    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={handleClose} />
      <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          비밀번호 변경
        </h3>

        {success ? (
          <div>
            <div className="rounded-md bg-green-50 border border-green-200 p-3 text-sm text-green-800 mb-6">
              비밀번호가 변경되었습니다. 다음 로그인부터 새 비밀번호를
              사용하세요.
            </div>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 cursor-pointer"
              >
                확인
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="space-y-4 mb-2">
              <div>
                <label
                  htmlFor="cp-current"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  현재 비밀번호
                </label>
                <input
                  id="cp-current"
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className={inputClass}
                  autoComplete="current-password"
                />
              </div>
              <div>
                <label
                  htmlFor="cp-new"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  새 비밀번호 (8자 이상)
                </label>
                <input
                  id="cp-new"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className={inputClass}
                  autoComplete="new-password"
                />
              </div>
              <div>
                <label
                  htmlFor="cp-confirm"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  새 비밀번호 확인
                </label>
                <input
                  id="cp-confirm"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className={inputClass}
                  autoComplete="new-password"
                />
              </div>
            </div>

            {error && (
              <p className="text-sm text-red-600 mt-3" role="alert">
                {error}
              </p>
            )}

            <div className="flex justify-end gap-2 mt-6">
              <button
                type="button"
                onClick={handleClose}
                disabled={isSubmitting}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-50 cursor-pointer disabled:opacity-50"
              >
                취소
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 cursor-pointer disabled:opacity-50"
              >
                {isSubmitting ? "변경 중..." : "변경"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
